<template>
<b-modal :id="id" v-model="visible" size="xl" title="Drug Change" hide-footer>
    <b-row class="mb-2">
        <b-col>
            <div v-html="prescription"/>
        </b-col>
    </b-row>
    <b-row id="details-row">

        <b-col id="current-drug">
            <b-row>
                <b-col>
                    <h6>Current Drug</h6>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Name:</strong>
                    {{ this.drugDetails && this.drugDetails.productNameShort }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>NDC:</strong>
                    {{ this.drugDetails && this.drugDetails.ndc }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>UPC:</strong>
                    {{ this.upc }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Package Size:</strong>
                    {{ this.drugDetails && `${this.drugDetails.packageSize} ${this.drugDetails.billingUnit}` }}
                </b-col>
            </b-row>
        </b-col>

        <b-col id="new-drug">
            <b-row>
                <b-col>
                    <h6>New Drug</h6>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <SearchPrescriptionDetailComponent v-model="newDrug"
                                                       :params="{onlyActive: true}"
                                                       :equivalent-drugs="equivalentDrugs"
                                                       label=""
                                                       search-u-r-l="/Drug"></SearchPrescriptionDetailComponent>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <EquivalentDrugsComponent :drug="drugDetails"
                                              prepopulate
                                              @drugsSelected="equivalentDrugsSearched"></EquivalentDrugsComponent>
                </b-col>
            </b-row>
            <b-row v-show="loadingNewDrugDetails">
                <b-col class="text-center">
                    <b-spinner label="Loading New Drug Data"></b-spinner>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>UPC:</strong>
                    {{ this.newUpc }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Package Size:</strong>
                    {{ this.newPackageSize }}
                </b-col>
            </b-row>
        </b-col>
    </b-row>

    <b-row v-show="processingNewBilling" id="processing-row">
        <b-col class="text-center">
            <b-spinner label="processing" variant="primary"></b-spinner>
        </b-col>
    </b-row>
    <b-row v-show="!processingNewBilling" id="buttons-row">
        <b-col cols="2" offset="10">
            <b-button variant="success" @click="changeDrug()" :disabled="!newDrug || !newDrug.drugId">Confirm</b-button>
        </b-col>
    </b-row>
</b-modal>
</template>

<script lang="ts">
import { Component, Emit, Prop, VModel, Vue, Watch } from "vue-property-decorator";
import { Prescription } from "@/models/Prescription";
import { Drug } from "@/models/Drug/Drug";
import { Identifier } from "@/models/GSDD";
import SearchComponent from "@/components/SearchComponent.vue";
import SearchPrescriptionDetailComponent from "@/components/Prescription/SearchPrescriptionDetailComponent.vue";
import Axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Claim } from "@/models/Claim";
import { PriorAuthorization } from "@/models/PriorAuthorization";
import EquivalentDrugsComponent from "@/components/Drug/EquivalentDrugsComponent.vue";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import { NonGSDDDrug } from "@/models/Drug/NonGSDDDrug";
import { CompoundDrug } from "@/models/Drug/CompoundDrug";

@Component({
    name: "DrugChangeModal",
    components: { EquivalentDrugsComponent, SearchComponent, SearchPrescriptionDetailComponent }
})
export default class DrugChangeModal extends Vue {
    @VModel({ type: Boolean, default: false }) visible!: boolean;
    @Prop({ default: 'drug-change-modal' }) private id!: string;
    @Prop({ type: Prescription, default: new Prescription() }) private prescription!: Prescription;
    @Prop() private drugDetails!: DrugProduct;
    private newDrug: DrugProduct = new Drug();
    private newDrugDetails: DrugProduct = new Drug();
    private equivalentDrugs: Drug.EquivalentDrug[] = [];

    private loadingNewDrugDetails = false;

    private processingNewBilling = false;

    get newNdcOrUpc(): string {
        const newNdc = this.newDrugDetails?.ndc;
        return newNdc?.length > 0 ? newNdc : this.newUpc;
    }

    get upc(): string {
        if (this.drugDetails?.source != DrugSource.GsddFederal) return '';
        const drug = this.drugDetails as Drug;
        let upc = drug?.packageDetail?.packageIdentifier?.filter((pi: Identifier) => pi.identifierType == 8);
        return upc?.length > 0 ? upc[0].identifier : '';
    }

    get newUpc(): string {
        if (this.newDrugDetails.source != DrugSource.GsddFederal) return '';
        const drug = this.newDrugDetails as Drug;
        let upc = drug?.packageDetail?.packageIdentifier?.filter((pi: Identifier) => pi.identifierType == 8);
        return upc?.length > 0 ? upc[0].identifier : '';
    }

    get newPackageSize(): string {
        return this.newDrugDetails?.packageSize ? `${this.newDrugDetails?.packageSize} ${this.newDrugDetails?.billingUnit}` : '';
    }

    @Watch('newDrug')
    newDrugUpdate(newVal: DrugProduct, oldVal: DrugProduct) {
        if (newVal.drugId == oldVal.drugId) return;
        const packId = newVal.drugId
        if (packId == undefined) return;
        this.loadingNewDrugDetails = true;
        Axios.get<DrugProduct>(`/Drug/${packId}`, { params: { drugSource: newVal.source, }, })
            .then(drugResponse => {
                if (drugResponse.data.source == DrugSource.GsddFederal) {
                    this.newDrugDetails = new Drug(packId, drugResponse.data);
                }
                if (drugResponse.data.source == DrugSource.NonGsddProduct) {
                    this.newDrugDetails = new NonGSDDDrug(packId, drugResponse.data);
                }
                if (drugResponse.data.source == DrugSource.CompoundProduct) {
                    this.newDrugDetails = new CompoundDrug(packId, drugResponse.data);
                }
            })
            .catch(err => this.$notification(NotificationOptions.error(err)))
            .finally(() => this.loadingNewDrugDetails = false);
    }

    changeDrug() {
        const url = `/Prescription/${this.prescription.storeID}/${this.prescription.rxNumber}/${this.prescription.rfNumber}/ChangeDrug`;
        const newDrugInfo = `NDC: ${this.newDrugDetails?.ndc} - Name: ${this.newDrugDetails?.productNameShort}`;

        // Sanitize the newDrug object to handle API data type requirements
        const sanitizedDrug = {
            ...this.newDrug,
            quantityOnHand: this.newDrug.quantityOnHand === "" ? null : this.newDrug.quantityOnHand
        };

        this.processingNewBilling = true;
        return Axios.post<{ claim: Claim, prescription: Prescription, pa: PriorAuthorization; }[]>(url, sanitizedDrug)
            .then(resp => {
                this.$notification(NotificationOptions.successSaveNotificationPreset("Drug Change"));
                this.$emit('change-drug', resp, newDrugInfo);
                return resp;
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
                this.$emit('change-drug', err, newDrugInfo);
                return err;
            })
            .finally(() => this.processingNewBilling = false);
    }

    equivalentDrugsSearched(drugs: Drug.EquivalentDrug[]) {
        this.equivalentDrugs = drugs;
    }
}
</script>

<style scoped>

</style>
